import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fixFalslyTranslatedMarkdown, useGlobalState } from "senf-shared";
import { LogoText } from "../../../assets/icons";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import Markdown from "../../core/richTextEditor/Markdown";
import Seperator from "../../core/seperator/Seperator";
import Tabs from "../../molecules/tabs/Tabs";

const PrivacyPolicy = ({ handleNavigateBack, app, url, project }) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (project?.title) {
      document.title = `${project.title} – ${t("privacyPolicy:title")}`;
    } else {
      document.title = t("privacyPolicy:title");
    }
  }, [project?.title, t]);

  const tabs = [
    {
      text: t("privacyPolicy:service_provider"),
      tab: "service-provider",
    },
    {
      text: t("privacyPolicy:publisher"),
      tab: "publisher",
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0].tab);

  const senfPrivacyPolicy = (
    <>
      <p className="body-lg-bold mb-1">{t("privacyPolicy:provider_name")}</p>
      <p className="body-lg mb-1">{t("privacyPolicy:provider_address")}</p>
      <p className="body-lg-underline mb-6">
        <a href="https://senf.app/" target="_blank" rel="noopener noreferrer">
          https://senf.app/
        </a>
      </p>

      <hr className="my-6" />

      <h2 className="heading-xl mb-4">{t("privacyPolicy:privacy_info")}</h2>
      <p className="body-lg mb-6">{t("privacyPolicy:intro_info")}</p>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:processed_data_types")}
      </h3>
      <ul className="body-lg mb-6 list-outside list-disc pl-5">
        <li>{t("privacyPolicy:contact_data")}</li>
        <li>{t("privacyPolicy:usage_data")}</li>
        <li>{t("privacyPolicy:demographic_data")}</li>
        <li>{t("privacyPolicy:device_data")}</li>
        <li>{t("privacyPolicy:tracking_data")}</li>
        <li>{t("privacyPolicy:activity_data")}</li>
      </ul>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:affected_persons")}</h3>
      <ul className="body-lg mb-6 list-outside list-disc pl-5">
        <li>{t("privacyPolicy:website_visitors")}</li>
        <li>{t("privacyPolicy:citizens")}</li>
      </ul>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:processing_purposes")}
      </h3>
      <ul className="body-lg mb-6 list-outside list-disc pl-5 ">
        <li>{t("privacyPolicy:provide_online_services")}</li>
        <li>{t("privacyPolicy:contractual_services")}</li>
        <li>{t("privacyPolicy:information_transfer")}</li>
        <li>{t("privacyPolicy:marketing")}</li>
        <li>{t("privacyPolicy:ai_processing")}</li>
      </ul>

      <h2 className="heading-xl mb-4">
        {t("privacyPolicy:rights_of_individuals")}
      </h2>
      <ul className="body-lg mb-6 list-outside list-disc pl-5">
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2528-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_access")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2614-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_rectification")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2621-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_erasure")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2818-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_objection")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2768-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_data_portability")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e6110-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_complaint")}
          </a>
        </li>
        <li>
          <a
            href="https://eur-lex.europa.eu/legal-content/DE/TXT/HTML/?uri=CELEX:32016R0679&from=EN#d1e2019-1-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("privacyPolicy:right_to_withdrawal")}
          </a>
        </li>
      </ul>

      <hr className="my-6" />

      <h2 className="heading-xl mb-4">1. {t("privacyPolicy:website_usage")}</h2>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:server_log_files")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:data_transmitted")}</p>
      <ul className="body-lg mb-6 list-outside list-disc pl-5">
        <li>{t("privacyPolicy:ip_address")}</li>
        <li>{t("privacyPolicy:referrer_url")}</li>
        <li>{t("privacyPolicy:access_time")}</li>
        <li>{t("privacyPolicy:browser_type")}</li>
        <li>{t("privacyPolicy:os_type")}</li>
        <li>{t("privacyPolicy:visit_duration")}</li>
      </ul>
      <p className="body-lg mb-6">{t("privacyPolicy:log_files_purpose")}</p>
      <p className="body-lg mb-6">
        {t("privacyPolicy:data_collection_purpose")}
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:registration")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:registration_details")}</p>

      <h2 className="heading-xl mb-4">
        2. {t("privacyPolicy:service_provision")}
      </h2>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:cloud_storage")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:google_firebase_usage")}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:google_firebase_privacy")}
        </a>
      </p>
      <p className="body-lg mb-6">
        {t("privacyPolicy:firebase_data_security")}
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:mapbox_usage")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:mapbox_note")}
        <a
          href="https://www.mapbox.com/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:mapbox_privacy")}
        </a>
      </p>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:ai_processing_section")}
      </h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:ai_processing_details")}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:google_vertex_privacy")}
        </a>
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:hosting_netlify")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:netlify_usage")}
        <a
          href="https://www.netlify.com/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:netlify_privacy")}
        </a>
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:email_hosting")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:ionos_usage")}
        <a
          href="https://www.ionos.de/terms-gtc/terms-privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:ionos_privacy")}
        </a>
      </p>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:additional_analytics")}
      </h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:hotjar_usage")}
        <a
          href="https://www.hotjar.com/legal/policies/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:hotjar_privacy")}
        </a>
      </p>
      <p className="body-lg mb-6">{t("privacyPolicy:utm_tracking")}</p>
      <p className="body-lg mb-6">
        {t("privacyPolicy:sentry_usage")}
        <a
          href="https://sentry.io/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("privacyPolicy:sentry_privacy")}
        </a>
      </p>

      <h2 className="heading-xl mb-4">3. {t("privacyPolicy:data_security")}</h2>
      <p className="body-lg mb-6">
        {t("privacyPolicy:data_security_description")}
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:data_retention")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:data_retention_details")}
      </p>

      <h2 className="heading-xl mb-4">
        4. {t("privacyPolicy:third_party_data_sharing")}
      </h2>
      <p className="body-lg mb-6">
        {t("privacyPolicy:third_party_data_sharing_description")}
      </p>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:data_transfer_third_countries")}
      </h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:data_transfer_third_countries_details")}
      </p>

      <h2 className="heading-xl mb-4">
        6. {t("privacyPolicy:regional_rights")}
      </h2>
      <p className="body-lg mb-6">{t("privacyPolicy:regional_rights_intro")}</p>

      <h3 className="heading-lg mb-2">
        {t("privacyPolicy:switzerland_rights")}
      </h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:switzerland_rights_details")}
      </p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:uk_rights")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:uk_rights_details")}</p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:usa_rights")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:usa_rights_details")}</p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:canada_rights")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:canada_rights_details")}</p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:brazil_rights")}</h3>
      <p className="body-lg mb-6">{t("privacyPolicy:brazil_rights_details")}</p>

      <h3 className="heading-lg mb-2">{t("privacyPolicy:global_contact")}</h3>
      <p className="body-lg mb-6">
        {t("privacyPolicy:global_contact_details")}
      </p>

      <h2 className="heading-xl mb-4">7. {t("privacyPolicy:cookie_config")}</h2>
      <p className="body-lg mb-6">
        {t("privacyPolicy:cookie_config_description")}
        <br /> <br />
        {app !== "dashboard" && (
          <a href={"?cookiesConfigurator=true"} rel="noopener noreferrer">
            {t("privacyPolicy:cookie_policy")}
          </a>
        )}
      </p>

      <h2 className="heading-xl mb-4">8. {t("privacyPolicy:contact_info")}</h2>
      <p className="body-lg mb-6">
        {t("privacyPolicy:contact_data_processing")}
      </p>

      <p className="body-xs text-right">
        {t("privacyPolicy:last_updated", { date: "April 2025" })}
      </p>
    </>
  );

  return (
    <div className="h-screen w-screen overflow-y-auto bg-[#fffefc]">
      <div className="ml-12 mt-7">
        <LogoText transform="scale(2)" />
      </div>
      <div className="mx-auto mt-48 max-w-3xl px-4 py-8">
        <Button
          variant="outline"
          leadingIcon="arrow-left-md"
          text={t("global:back")}
          onClick={handleNavigateBack}
        />
        <h1
          className={cn(
            "my-6 font-black",
            isMobile ? "heading-2xl" : "heading-5xl",
          )}
        >
          {t("privacyPolicy:title")}
        </h1>
        <p className="body-lg mb-6 text-wrap break-all">
          {t("privacyPolicy:subtitle", { val: url })}
        </p>

        {project?.settings?.privacyPolicy?.publisher ||
        project?.settings?.privacyPolicy?.content ? (
          <>
            <Tabs
              active={activeTab}
              setActive={setActiveTab}
              classNames={{
                tab: "heading-xl",
                wrapper: "mb-6 h-12 max-w-full",
              }}
              withBorder
              tabs={tabs}
            />
            {activeTab === "publisher" ? (
              <>
                <Markdown className="text-lg leading-7 whitespace-pre-wrap">
                  {fixFalslyTranslatedMarkdown(
                    translation?.[
                      project?.settings?.privacyPolicy?.publisher
                    ] || project?.settings?.privacyPolicy?.publisher,
                  )}
                </Markdown>
                {project?.settings?.privacyPolicy?.publisher &&
                project?.settings?.privacyPolicy?.content ? (
                  <Seperator className="py-4" />
                ) : null}

                <Markdown className="text-lg leading-7 whitespace-pre-wrap">
                  {fixFalslyTranslatedMarkdown(
                    translation?.[project?.settings?.privacyPolicy?.content] ||
                      project?.settings?.privacyPolicy?.content,
                  )}
                </Markdown>
              </>
            ) : (
              senfPrivacyPolicy
            )}
          </>
        ) : (
          <>
            <h2 className="heading-xl mb-4">
              {t("privacyPolicy:service_provider")}
            </h2>

            {senfPrivacyPolicy}
          </>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
